import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import ArrowDown from '@ui/icons/imaterial/arrows/Arrow Down.svg';
import FiltersIcon from '@ui/icons/imaterial/base/Filters Vertical.svg';
import theme from '@ui/theme';
import { ParsedUrlQuery } from 'querystring';
import React, { useState } from 'react';

import useStyles from './FilterControls.style';

type FilterControlsProps = {
  filters: any[];
  resultsCount?: number;
  pageReset?: () => void;
  initQuery?: ParsedUrlQuery;
  expandedAccordion?: boolean;
}

const FilterControls: React.FC<FilterControlsProps> = (props: FilterControlsProps) => {
  const { classes, cx } = useStyles();
  const { md } = useBreakpoints();

  const [openFilters, setOpenFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<number>(0);

  const clearFilters = () => {
    Object.keys(props.filters).map(filter => {
      props.filters[filter].setter(undefined);

      if (props.filters[filter].setter2) {
        props.filters[filter].setter2(undefined);
      }
    });
    setAppliedFilters(0);
  };

  return (
    <div>
      {!md &&
        <Button
          size="small"
          variant="outlined"
          color="primary"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: theme.palette.common.white,
            border: '1px solid #DDDDDD',
            padding: theme.spacing(0.5, 1),
          }}
          onClick={() => setOpenFilters(true)}
        >
          <Typography>
            Filter
          </Typography>
          {appliedFilters ?
            <div className={classes.count}>
              {appliedFilters}
            </div>
            : <FiltersIcon style={{ height: 16, width: 16 }} />
          }
        </Button>
      }
      <div
        className={cx({
          [classes.filtersContainer]: !md,
          [classes.filtersContainerOpen]: openFilters && !md,
        })}
      >
        {!md &&
          <div className={classes.filtersContainerHeader}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                style={{ color: theme.palette.common.black }}
                size="large"
                onClick={() => setOpenFilters(false)}
              >
                <CloseIcon style={{ height: 30, width: 30 }} />
              </IconButton>
              <Typography
                fontFamily={theme.gh_vars.circular}
                style={{ fontSize: 24, display: 'inline-block' }}
              >
                Filter results
              </Typography>
            </div>
            <Typography
              fontFamily={theme.gh_vars.circular}
              style={{ fontSize: 16, marginTop: theme.spacing(2), marginRight: theme.spacing(2) }}
            >
              {props.resultsCount}
              {' '}
              {props.resultsCount === 1 ? 'Item' : 'Items'}
            </Typography>
          </div>
        }
        <div
          style={{
            overflow: md ? 'hidden' : 'scroll',
            margin: md ? 0 : theme.spacing(0, 3, 7)
          }}
        >
          {props.filters.map((filter) => {
            const {
              id,
              title,
              options,
              values,
              values2,
              counts,
              counts2,
              setter,
              setter2
            } = filter;

            let expanded;

            if (props.initQuery) {
              const selectedValues = Object.values(props.initQuery);

              if (selectedValues[0]?.includes(filter?.values?.[0])) {
                expanded = true;
              }
            }

            if (props.expandedAccordion) {
              expanded = true;
            }

            if ((!counts || !Object.keys(counts).length) && id !== 'price') {
              return null;
            }

            return (
              <Accordion
                key={id}
                square
                className={classes.filterAccordion}
                defaultExpanded={expanded}
              >
                <AccordionSummary
                  expandIcon={<ArrowDown className={classes.filterAccordionIcon} />}
                  aria-controls={`${id}-content`}
                  id={`${id}-header`}
                >
                  <Typography
                    className={classes.filterAccordionSummary}
                  >
                    {title}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails className={classes.filterAccordionDetails}>
                  {
                    options.map((option) => {
                      const count = counts && counts[option.id];

                      if (!count && id !== 'price') {
                        return null;
                      }

                      let checked = false;

                      if (Array.isArray(values)) {
                        checked = values.indexOf(option.id) !== -1;
                      } else {
                        checked = values === option.id;
                      }

                      return (
                        <>
                          <div
                            key={option.id}
                            style={{ paddingRight: theme.spacing(0.2) }}
                          >
                            <FormControlLabel
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                              control={
                                <Switch
                                  checked={checked}
                                  name={option.id}
                                  onChange={(e) => {
                                    props.pageReset();
                                    if (e.target.checked) {
                                      if (Array.isArray(values)) {
                                        setter([...values, option.id]);
                                        setAppliedFilters(appliedFilters + 1);
                                      } else {
                                        setter([values, option.id].filter(i => i));
                                        setAppliedFilters(appliedFilters + 1);
                                      }
                                    } else {
                                      if (Array.isArray(values)) {
                                        setter(values.filter(i => i !== option.id));
                                        setAppliedFilters(appliedFilters - 1);
                                        if (setter2) {
                                          setter2(undefined);
                                          setAppliedFilters(appliedFilters - 1);
                                        }
                                      } else {
                                        setter(undefined);
                                        setAppliedFilters(appliedFilters - 1);
                                        if (setter2) {
                                          setter2(undefined);
                                          setAppliedFilters(appliedFilters - 1);
                                        }
                                      }
                                    }
                                  }}

                                />
                              }
                              label={option.title}
                              labelPlacement="start"
                            />
                          </div>
                          {option.subcategories && checked &&
                            option.subcategories.map((subcategory) => {

                              if (!subcategory.public || !counts2?.[subcategory.id]) {
                                return null;
                              }

                              let checked = false;

                              if (Array.isArray(values2)) {
                                checked = values2?.indexOf(subcategory.id) !== -1;
                              } else {
                                checked = values2 === subcategory.id;
                              }

                              return (
                                <div
                                  key={subcategory.id}
                                  style={{ marginLeft: theme.spacing(1.5), paddingRight: theme.spacing(0.2) }}
                                >
                                  <FormControlLabel
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between'
                                    }}
                                    control={
                                      <Switch
                                        checked={checked}
                                        name={subcategory.id}
                                        onChange={(e) => {
                                          props.pageReset();
                                          if (e.target.checked) {
                                            if (Array.isArray(values2)) {
                                              setter2([...values2, subcategory.id]);
                                              setAppliedFilters(appliedFilters + 1);
                                            } else {
                                              setter2([values2, subcategory.id].filter(i => i));
                                              setAppliedFilters(appliedFilters + 1);
                                            }
                                          } else {
                                            if (Array.isArray(values2)) {
                                              setter2(values2.filter(i => i !== subcategory.id));
                                              setAppliedFilters(appliedFilters - 1);
                                            } else {
                                              setter2(undefined);
                                              setAppliedFilters(appliedFilters - 1);
                                            }
                                          }
                                        }}

                                      />
                                    }
                                    label={subcategory.title}
                                    labelPlacement="start"
                                  />
                                </div>
                              );
                            }
                            )
                          }
                        </>
                      );
                    }
                    )
                  }
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      </div>
      {openFilters &&
        <div
          className={classes.filterContainerButtons}
        >
          <Button
            size="large"
            variant="contained"
            color="secondary"
            style={{ width: '100%', marginBottom: theme.spacing(2), border: '2px solid' }}
            onClick={() => {
              clearFilters();
            }}
          >
            <Typography
              fontFamily={theme.gh_vars.circular}
              style={{ fontSize: 14, lineHeight: 2 }}
            >
              Clear filters
            </Typography>
          </Button>
          <Button
            size="large"
            variant="contained"
            color="secondary"
            style={{ width: '100%', marginBottom: theme.spacing(3), border: '2px solid' }}
            onClick={() => setOpenFilters(false)}
          >
            <Typography
              fontFamily={theme.gh_vars.circular}
              style={{ fontSize: 14, lineHeight: 2 }}
            >
              Apply filters
            </Typography>
          </Button>
        </div>
      }
    </div>
  );
};

FilterControls.defaultProps = {
  filters: [],
};

export default FilterControls;
